import { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import API_URLS from "../../../../constants/apiConstants";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import styles from "../../css/activityReport.module.css";
import { DatePicker } from "../../../../components/components/js/DatePicker";
import { GeneralDropdown } from "../../../../components/components/js/GeneralDropdown";
import { LoadingDots } from "../../../../components/general";
import { useTranslation } from "react-i18next";

export const ComparativeReport = ({ reportRef }) => {
  const { t, i18n } = useTranslation();
  const cardTitlesPrefix = "admin_pages.quran_report.card_titles";
  const tableColsPrefix = "admin_pages.quran_report.comparative_table_cols";
  const filtersPrefix = "admin_pages.quran_report.filters";
  const sortOptionsPrefix = `${filtersPrefix}.sort_options`;

  const trans = {
    comparativeReport: t(`${cardTitlesPrefix}.comparative_report`),
    noRecords: t(`${cardTitlesPrefix}.no_records`),
    sort: t(`${filtersPrefix}.sort`),
    sortOptions: t(`${filtersPrefix}.sort_options`),
    startDate: t(`${filtersPrefix}.start_date`),
    endDate: t(`${filtersPrefix}.end_date`),
    selectRoom: t(`${filtersPrefix}.select_room`),
    selectStaff: t(`${filtersPrefix}.select_staff`),
    selectRoomFirst: t(`${filtersPrefix}.select_room_first`),
    noStaff: t(`${filtersPrefix}.no_staff`),
    requiredMsg: t(`${filtersPrefix}.select_this_field`),
    filterBtn: t("buttons.filter"),
    filterLoadingBtn: t("buttons.filtering"),
  };

  const tableColumns = [
    { key: "student_name", label: t(`${tableColsPrefix}.student_name`) },
    { key: "current_sura", label: t(`${tableColsPrefix}.current_sura`) },
    { key: "memorized_suwar", label: t(`${tableColsPrefix}.memorized_suwar`) },
    { key: "memorized_ayat", label: t(`${tableColsPrefix}.memorized_ayat`) },
    {
      key: "memorization_avg",
      label: t(`${tableColsPrefix}.memorization_avg`),
    },
    { key: "revision_avg", label: t(`${tableColsPrefix}.revision_avg`) },
    { key: "mistakes", label: t(`${tableColsPrefix}.mistakes`) },
  ];

  const orderOptions = [
    {
      value: "alphabetically",
      label: t(`${sortOptionsPrefix}.alphabetically`),
    },
    {
      value: "current_sura_down",
      label: t(`${sortOptionsPrefix}.current_sura_down`),
    },
    {
      value: "current_sura_up",
      label: t(`${sortOptionsPrefix}.current_sura_up`),
    },
    { value: "most_mistakes", label: t(`${sortOptionsPrefix}.most_mistakes`) },
    {
      value: "least_mistakes",
      label: t(`${sortOptionsPrefix}.least_mistakes`),
    },
    {
      value: "most_completed_suwar",
      label: t(`${sortOptionsPrefix}.most_completed_suwar`),
    },
    { value: "highest_score", label: t(`${sortOptionsPrefix}.highest_score`) },
  ];

  const [orderParam, setOrderParam] = useState(orderOptions[0]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  // const [selectedStaff, setSelectedStaff] = useState(null);
  // const [staff, setStaff] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingRooms, setLoadingRooms] = useState(false);
  // const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);

  const { school } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  // Create sorted data based on orderParam
  const sortedTableData = useMemo(() => {
    if (!orderParam || !tableData.length) return tableData;

    return [...tableData].sort((a, b) => {
      switch (orderParam.value) {
        case "alphabetically":
          return a.student_name.localeCompare(b.student_name);

        case "current_sura_up":
          return b.current_sura_number - a.current_sura_number;

        case "current_sura_down":
          return a.current_sura_number - b.current_sura_number;

        case "most_mistakes":
          return b.mistakes - a.mistakes;

        case "least_mistakes":
          return a.mistakes - b.mistakes;

        case "most_completed_suwar":
          return b.memorized_suwar - a.memorized_suwar;

        case "highest_score":
          return b.memorization_avg - a.memorization_avg;

        default:
          return 0;
      }
    });
  }, [tableData, orderParam]);

  const handleFilter = useCallback(async () => {
    const newErrors = {
      room: !selectedRoom,
      // staff: !selectedStaff,
      startDate: !selectedStartDate,
      endDate: !selectedEndDate,
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some(Boolean);
    if (hasError) return;

    // All fields are valid
    const fetchData = async () => {
      const searchParams = {
        roomId: selectedRoom.value,
        // staffId: selectedStaff.value,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      };
      const url = API_URLS.getComparativeReport(searchParams);
      const response = await axiosPrivate.get(url);
      return response.data;
    };

    try {
      setLoadingResult(true);
      const data = await fetchData();
      const updatedData = data.map((item) => ({
        ...item,
        current_sura:
          i18n.language === "en" ? item.current_sura : item.current_sura_ar,
      }));
      setTableData(updatedData);
    } catch (err) {
      // Do nothing
    } finally {
      setLoadingResult(false);
    }
  }, [
    axiosPrivate,
    i18n.language,
    selectedEndDate,
    selectedRoom,
    selectedStartDate,
  ]);

  // Set start & end of the current month at the first render
  useEffect(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const format = (date) => date.toISOString().split("T")[0];

    setSelectedStartDate(format(start));
    setSelectedEndDate(format(end));
  }, []);

  useEffect(() => {
    if (selectedRoom && selectedStartDate && selectedEndDate) {
      handleFilter();
    }
  }, [selectedRoom, selectedStartDate, selectedEndDate, handleFilter]);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        setLoadingRooms(true);
        const api_url = API_URLS.getSchoolRooms(school.id);
        const response = await axiosPrivate.get(api_url);
        const data = response?.data?.map((room) => ({
          value: room.id,
          label: room.ClassName,
        }));

        setRooms(data);
        setSelectedRoom(data?.[0]);
      } catch (error) {
        // Do nothing
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
  }, [axiosPrivate, school.id]);

  // useEffect(() => {
  //   const fetchStaff = async () => {
  //     if (!selectedRoom) return;

  //     try {
  //       setLoadingStaff(true);
  //       const api_url = API_URLS.getRoomStaff(selectedRoom?.value);
  //       const response = await axiosPrivate.get(api_url);
  //       const data = response?.data?.staff?.map((staff) => ({
  //         value: staff.id,
  //         label: staff.name,
  //       }));

  //       setStaff(data);
  //     } catch (error) {
  //       // Do nothing
  //     } finally {
  //       setLoadingStaff(false);
  //     }
  //   };

  //   fetchStaff();
  // }, [axiosPrivate, selectedRoom]);

  // Clear staff data on Room change
  // useEffect(() => {
  //   setStaff([]);
  //   setSelectedStaff(null);
  // }, [selectedRoom]);

  return (
    <div className={styles.container}>
      <div className={styles.filtersContainer}>
        <GeneralDropdown
          placeholder={trans.sort}
          options={orderOptions}
          selectedOption={orderParam}
          onChange={(option) => setOrderParam(option)}
          isClearable
        />

        <GeneralDropdown
          placeholder={trans.selectRoom}
          options={rooms}
          selectedOption={selectedRoom}
          onChange={(option) => setSelectedRoom(option)}
          errMsg={errors.room && trans.requiredMsg}
          isSearchable
          disabled={loadingRooms}
        />

        {/* <GeneralDropdown
            placeholder={trans.selectStaff}
            options={staff}
            selectedOption={selectedStaff}
            onChange={(option) => setSelectedStaff(option)}
            noOptionsMessage={
              !selectedRoom ? trans.selectRoomFirst : trans.noStaff
            }
            errMsg={errors.staff && trans.requiredMsg}
            isSearchable
            disabled={loadingStaff}
          /> */}

        <DatePicker
          label={trans.startDate}
          date={selectedStartDate}
          setDate={setSelectedStartDate}
          errMsg={errors.startDate && trans.requiredMsg}
        />
        <DatePicker
          label={trans.endDate}
          date={selectedEndDate}
          setDate={setSelectedEndDate}
          errMsg={errors.endDate && trans.requiredMsg}
          minDate={selectedStartDate}
        />
        {/* <Button onClick={handleFilter} disabled={loadingResult}>
            {loadingResult ? trans.filterLoadingBtn : trans.filterBtn}
          </Button> */}
      </div>

      {loadingResult ? (
        <LoadingDots className={styles.smallLoadingContainer} />
      ) : (
        <div ref={reportRef}>
          <Row>
            <Col>
              <div className={styles.card}>
                <p className={styles.title}>{trans.comparativeReport}</p>
                <table className={`text-color text-center ${styles.table}`}>
                  <thead className={styles.tableHead}>
                    <tr>
                      {tableColumns.map((col, index) => (
                        <th key={index} className="p-2 fw-bold text-12">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!sortedTableData.length && (
                      <tr>
                        <td
                          className="py-4 w-100 text-center"
                          colSpan={tableColumns.length}
                          style={{ color: "#666" }}
                        >
                          {trans.noRecords}
                        </td>
                      </tr>
                    )}
                    {sortedTableData?.map((row, index) => (
                      <tr key={index}>
                        {tableColumns.map((col, i) => (
                          <td className="p-2 fw-semibold text-12" key={i}>
                            <span
                              className={
                                col.key === "memorization_avg"
                                  ? styles.green
                                  : col.key === "revision_avg"
                                  ? styles.blue
                                  : col.key === "mistakes"
                                  ? styles.red
                                  : null
                              }
                            >
                              {["memorization_avg", "revision_avg"].includes(
                                col.key
                              )
                                ? parseFloat(row[col.key].toFixed(1))
                                : row[col.key]}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};
